/**
 * Breadth data store — holds screener_counts rows for use by
 * the MA_BREADTH and WEEK52_HLN chart sub-pane indicators.
 *
 * Persisted to localStorage so data survives hard refreshes without an extra
 * API call. localStorage rehydration is synchronous, so by the time any
 * createIndicator() call runs, calc() already has real data.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ScreenerCountsRow } from '@/types';
import { api } from '@/services/api';

function buildIndex(rows: ScreenerCountsRow[]): Map<number, number> {
  const idx = new Map<number, number>();
  rows.forEach((r, i) => {
    // D1 trade_date is "YYYY-MM-DD" UTC midnight
    const ms = new Date(r.trade_date + 'T00:00:00Z').getTime();
    idx.set(ms, i);
  });
  return idx;
}

interface BreadthState {
  /** All screener_counts rows, sorted ASC by trade_date. Empty until fetched. */
  data: ScreenerCountsRow[];
  /** Millisecond timestamp → index into data, for fast O(1) lookup by chart bar. */
  tsIndex: Map<number, number>;
  loading: boolean;
  error: string | null;
  fetched: boolean;

  setData: (rows: ScreenerCountsRow[]) => void;
  setLoading: (v: boolean) => void;
  setError: (e: string | null) => void;
}

export const useBreadthStore = create<BreadthState>()(
  persist(
    (set) => ({
      data: [],
      tsIndex: new Map(),
      loading: false,
      error: null,
      fetched: false,

      setData(rows) {
        set({ data: rows, tsIndex: buildIndex(rows), fetched: true, error: null });
      },
      setLoading: (loading) => set({ loading }),
      setError:   (error)   => set({ error }),
    }),
    {
      name: 'cseview-breadth',
      version: 1, // bumped to invalidate stale localStorage (was 0/unversioned)
      // Only persist the raw rows. Map<> is not JSON-serializable; tsIndex is
      // rebuilt synchronously in the block below after the store is created.
      partialize: (state) => ({ data: state.data }),
    },
  ),
);

// Synchronous rehydration: rebuild tsIndex + mark fetched=true from any rows
// that were loaded from localStorage. This runs before any React component
// mounts, so calc() already has real data the first time createIndicator fires.
{
  const { data } = useBreadthStore.getState();
  if (data.length > 0) {
    useBreadthStore.setState({ tsIndex: buildIndex(data), fetched: true });
  }
}

/** Returns today's date string (YYYY-MM-DD) in Asia/Colombo timezone. */
function todayColombo(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Colombo' });
}

/**
 * Fetch breadth data if not already loaded or if the cached data doesn't
 * include yesterday/today's trading date.
 *
 * Staleness is data-driven: we compare the latest row's trade_date to today
 * in Colombo time rather than relying on a wall-clock TTL.  This means:
 *   – Old localStorage entries with no fetchedAt always re-fetch.
 *   – After a successful fetch, same-day revisits are instant (cache hit).
 *   – The next calendar day in Colombo, the first add triggers a fresh fetch.
 *
 * Returns true when data is ready, false on network error.
 */
export async function ensureBreadthData(): Promise<boolean> {
  const { data, fetched, loading, setData, setLoading, setError } = useBreadthStore.getState();
  const latestRow  = data.length > 0 ? data[data.length - 1].trade_date : null;
  const today      = todayColombo();
  // Stale if: never fetched, empty, or latest cached row is before today
  // (weekends/holidays are fine — market just didn't trade, no new row exists)
  const stale = !fetched || latestRow == null || latestRow < today;
  if (!stale) return true;
  if (loading) {
    return new Promise((resolve) => {
      const unsub = useBreadthStore.subscribe((s) => {
        if (!s.loading) { unsub(); resolve(s.fetched); }
      });
    });
  }
  setLoading(true);
  try {
    const res = await api.screenerCounts(1000);
    setData(res.data);
    return true;
  } catch (e) {
    setError(e instanceof Error ? e.message : 'Failed to load breadth data');
    return false;
  } finally {
    setLoading(false);
  }
}
